import BillboardLocations from 'pages/MasterCampaignPlanPage/new/components/Projections/BillboardLocations'
import TargetedHouseholdLocations from 'pages/MasterCampaignPlanPage/new/components/Projections/TargetedHouseholdLocations'
import { FC } from 'react'

import { EuiSpacer, EuiLoadingContent } from '@elastic/eui'

import { useGetBillboardsByZipCodesQuery } from 'api/billboardApi'
import { useGetDomainsQuery } from 'api/domainsApi'
import { MasterCampaignPlan } from 'api/entities/MasterCampaignPlan'
import { MasterCampaignStub, MasterCampaignPlanCampaignBuilderType } from 'api/entities/MasterCampaignStub'
import { useHouseholdCensusData } from 'utils/useHouseholdCensusData'
import { useBillboardZipCodes, useHouseholdZipCodes } from 'utils/useProjectionsUtils'

import TabPageHeader from '../TabPageHeader'
import DeliveryMetrics from './DeliveryMetrics'
import PlacementCharts from './PlacementCharts'

export interface ProjectionsProps {
  masterCampaignPlan: MasterCampaignPlan
  masterCampaignStub: MasterCampaignStub
}

const Projections: FC<ProjectionsProps> = ({ masterCampaignPlan, masterCampaignStub }) => {
  const billboardZipCodes = useBillboardZipCodes(masterCampaignStub)
  const householdZipCodes = useHouseholdZipCodes(masterCampaignStub)
  const section = masterCampaignPlan.projectionsSection

  const { isFetching: isBillboardDataFetching } = useGetBillboardsByZipCodesQuery({ zipCodes: billboardZipCodes }, { skip: billboardZipCodes.length === 0 })
  const { isWaiting: isCensusDataWaiting, hasCensusData } = useHouseholdCensusData(householdZipCodes)
  const { isFetching: isDomainsFetching } = useGetDomainsQuery()

  const isProjectionsDataLoading = isBillboardDataFetching || isCensusDataWaiting || isDomainsFetching

  const pageTitle = section?.pageTitle || 'Projections'
  const hasInternet = masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Internet)
  const hasTv = masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.TV)

  if (isProjectionsDataLoading) {
    return (
      <>
        <TabPageHeader title={pageTitle} />
        <EuiLoadingContent lines={10} />
      </>
    )
  }

  return (
    <>
      <TabPageHeader title={pageTitle} />

      <DeliveryMetrics masterCampaignStub={masterCampaignStub} masterCampaignPlan={masterCampaignPlan} section={section?.cpmAndImpressions} />

      {masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Internet || cb.campaignType === MasterCampaignPlanCampaignBuilderType.TV) && (
        <>
          <EuiSpacer size='l' />

          <PlacementCharts masterCampaignPlan={masterCampaignPlan} hasInternet={hasInternet} hasTv={hasTv} whereAdsAppearTitle={section?.whereAdsAppearTitle} auctionBasedPlacementsSection={section?.auctionBasedPlacements} ctvSection={section?.ctvPlacementCategories} internetSection={section?.internetAdPlacements} />

          {hasCensusData && (
            <>
              <EuiSpacer size='l' />

              <TargetedHouseholdLocations masterCampaignStub={masterCampaignStub} section={section?.geographicDistribution} />
            </>
          )}
        </>
      )}

      {masterCampaignStub.campaignBuilders.some(cb => cb.campaignType === MasterCampaignPlanCampaignBuilderType.Billboard) && (
        <>
          <EuiSpacer size='l' />

          <BillboardLocations masterCampaignStub={masterCampaignStub} section={section?.billboardLocations} />
        </>
      )}
    </>
  )
}

export default Projections
